import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { FieldPath, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import AccessStep from 'src/features/membershipType/components/wizard/step/AccessStep.tsx';
import BasicsStep from 'src/features/membershipType/components/wizard/step/BasicsStep';
import OverviewStep from 'src/features/membershipType/components/wizard/step/OverviewStep';
import PricingStep from 'src/features/membershipType/components/wizard/step/PricingStep';
import ValidityStep from 'src/features/membershipType/components/wizard/step/ValidityStep';
import { membershipTypeFormSchema, MembershipTypeFormSchemaValues } from 'src/features/membershipType/formSchema';
import { MEMBERSHIP_TEMPLATE_CATEGORY, MembershipType } from 'src/features/membershipType/types';
import { STEP_FIELDS, WizardStep, STEP_LABEL } from 'src/features/membershipType/utils/constants';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { Form } from 'src/registry/ui/Form';
import {
    Wizard,
    WizardContent,
    WizardList,
    WizardTrigger,
    WizardTriggerMessage,
    WizardTriggerNode,
} from 'src/registry/ui/Wizard';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { commonMessages } from 'src/utils/messages';

type Props = {
    selectedMembershipTemplate?: MembershipType;
    className?: string;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    title: string;
    mutate: (
        data: MembershipTypeFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
};

type Error = {
    attr: FieldPath<MembershipTypeFormSchemaValues>;
    code: string;
    detail: string;
};

// Backend attrs can be nested (`translations.0.name`), so match on the top-level field name.
const isStepError = (step: WizardStep, error: Error) =>
    STEP_FIELDS[step].some((field) => error.attr === field || error.attr.startsWith(`${field}.`));

export default function MembershipTypeWizardForm({
    selectedMembershipTemplate,
    className,
    isPending,
    children,
    title,
    mutate,
}: Props) {
    const [step, setStep] = React.useState(WizardStep.BASICS);
    const [errors, setErrors] = React.useState<Error[]>([]);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const onError = useOnError();

    const { data: membershipTypeData } = useGetMembershipType({
        parkingId,
        membershipTypeId: selectedMembershipTemplate?.id ?? '',
        config: { enabled: !!selectedMembershipTemplate?.id && isModalOpen },
    });

    const defaultValues = React.useMemo(
        () =>
            membershipTypeData
                ? {
                      ...membershipTypeData,
                      site: membershipTypeData.site.id,
                      limit_to_roles: membershipTypeData.limit_to_roles.map((role) => role.id),
                      custom_duration: membershipTypeData.limit_duration && membershipTypeData.duration === null,
                  }
                : {
                      translations: [],
                      has_pricing_plan: false,
                      has_time_restriction: false,
                      apply_pricing_per_space: false,
                      allow_custom_space_count_definition: false,
                      limit_duration: false,
                      allow_access_over_capacity_limit: false,
                      contribute_to_available_spaces: false,
                      allow_extra_visitors: false,
                      auto_renew: false,
                      allow_access_as_client_outside_restrictions: false,
                      custom_duration: false,
                      has_allowed_spaces_count: false,
                      allow_custom_time_restriction: false,
                  },
        [membershipTypeData],
    );

    const form = useForm<MembershipTypeFormSchemaValues>({
        resolver: zodResolver(membershipTypeFormSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
        setStep(WizardStep.BASICS);
        setErrors([]);
    }, [membershipTypeData, isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: MembershipTypeFormSchemaValues) => {
            const { custom_duration: _, ...rest } = values;
            mutate(
                rest,
                () => setIsModalOpen(false),
                (error) => {
                    if (error?.response?.data?.errors) setErrors(error.response.data.errors as Error[]);
                    onError(error, form.setError);
                },
            );
        },

        [mutate, form, onError],
    );

    const isAriaInvalid = React.useCallback(
        (step: WizardStep) => errors.some((field) => isStepError(step, field)),
        [errors],
    );

    const {
        limit_duration,
        custom_duration,
        has_pricing_plan,
        has_allowed_spaces_count,
        allow_custom_space_count_definition,
        allow_extra_visitors,
        has_time_restriction,
        category,
    } = form.watch();

    // The condition is on purpose in a separate use effect
    React.useEffect(() => {
        // Prevents executing on mount
        if (!form.formState.isDirty) return;

        if (category === MEMBERSHIP_TEMPLATE_CATEGORY.SUBSCRIPTION) {
            form.setValue('has_pricing_plan', true);
            form.setValue('limit_duration', true);
        } else {
            form.setValue('has_pricing_plan', null);
            form.setValue('limit_duration', null);
        }
    }, [category, form]);

    React.useEffect(() => {
        const clearedFields: FieldPath<MembershipTypeFormSchemaValues>[] = [];

        if (!limit_duration) {
            form.setValue('duration_unit', null);
            form.setValue('duration', null);
            form.setValue('custom_duration', null);
            clearedFields.push('duration_unit', 'duration', 'custom_duration');
        }
        if (limit_duration && custom_duration) {
            form.setValue('duration_unit', null);
            form.setValue('duration', null);
            clearedFields.push('duration_unit', 'duration');
        }
        if (!has_pricing_plan) {
            form.setValue('apply_pricing_per_space', false);
            form.setValue('auto_renew', false);
            form.setValue('pricing_plan_identity', null);
            clearedFields.push('apply_pricing_per_space', 'auto_renew', 'pricing_plan_identity');
        }
        if (!has_allowed_spaces_count) {
            form.setValue('allow_custom_space_count_definition', false);
            form.setValue('allow_extra_visitors', false);
            form.setValue('max_allowed_spaces_count', null);
            clearedFields.push(
                'allow_custom_space_count_definition',
                'allow_extra_visitors',
                'max_allowed_spaces_count',
            );
        }
        if (has_allowed_spaces_count && allow_custom_space_count_definition) {
            form.setValue('max_allowed_spaces_count', null);
            clearedFields.push('max_allowed_spaces_count');
        }
        if (!allow_extra_visitors) {
            form.setValue('max_extra_visitors_count', null);
            clearedFields.push('max_extra_visitors_count');
        }
        if (!has_time_restriction) {
            form.setValue('allow_custom_time_restriction', false);
            clearedFields.push('allow_custom_time_restriction');
        }

        if (clearedFields.length > 0) form.clearErrors(clearedFields);
    }, [
        form,
        limit_duration,
        custom_duration,
        has_pricing_plan,
        has_allowed_spaces_count,
        allow_custom_space_count_definition,
        allow_extra_visitors,
        has_time_restriction,
        step,
    ]);

    const goToNextStep = async () => {
        const isValid = await form.trigger(STEP_FIELDS[step]);
        const newStep = step + 1;

        if (isValid && newStep < 6) {
            setErrors(errors.filter((error) => !isStepError(newStep, error)));
            setStep(newStep);
        }
        // if not valid, errors are already populated in formState.errors
    };

    const goToStep = (step: WizardStep) => {
        setErrors(errors.filter((error) => !isStepError(step, error)));
        setStep(step);
    };

    const goToPreviousStep = () => {
        if (step === WizardStep.BASICS) return onOpenChange(false);
        const newStep = step - 1;

        if (newStep > 0) {
            setErrors(errors.filter((error) => !isStepError(newStep, error)));
            setStep((prevState) => (prevState > 1 ? prevState - 1 : prevState));
        }
    };

    return (
        <Wizard value={step.toString()}>
            <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
                {children ? (
                    <DialogTrigger asChild>{children}</DialogTrigger>
                ) : (
                    <DialogTrigger asChild>
                        <Button className={className}>
                            <FontAwesomeIcon icon={ICON_MAP.ADD} />
                            {title}
                        </Button>
                    </DialogTrigger>
                )}
                <DialogContent className="h-9/10 max-w-5xl">
                    <DialogHeader>
                        <div className="flex flex-col">
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    {...membershipTypeMessages.stepDescription}
                                    values={{
                                        currentStep: step,
                                        lastStep: WizardStep.OVERVIEW.toString(),
                                        label: formatMessage(STEP_LABEL[step]),
                                    }}
                                />
                            </DialogDescription>
                        </div>
                        <WizardList className="relative h-auto w-full justify-between overflow-x-auto border-0 shadow-none">
                            <div className="absolute inset-x-5 top-4 -z-10 mx-auto h-1 bg-gray-200">
                                <hr
                                    className="bg-primary-600 h-full transition-[width] duration-500"
                                    style={{ width: `${((step - 1) * 100) / 4}%` }}
                                />
                            </div>
                            <WizardTrigger
                                value={WizardStep.BASICS.toString()}
                                className="pr-4"
                                onClick={() => goToStep(WizardStep.BASICS)}
                            >
                                <WizardTriggerNode aria-invalid={isAriaInvalid(WizardStep.BASICS)}>
                                    {WizardStep.BASICS}
                                </WizardTriggerNode>
                                <WizardTriggerMessage>
                                    <FormattedMessage {...membershipTypeMessages.basics} />
                                </WizardTriggerMessage>
                            </WizardTrigger>
                            <WizardTrigger
                                value={WizardStep.ACCESS.toString()}
                                disabled={step < WizardStep.ACCESS}
                                onClick={() => goToStep(WizardStep.ACCESS)}
                                className="px-4"
                            >
                                <WizardTriggerNode aria-invalid={isAriaInvalid(WizardStep.ACCESS)}>
                                    {WizardStep.ACCESS}
                                </WizardTriggerNode>
                                <WizardTriggerMessage>
                                    <FormattedMessage {...membershipTypeMessages.access} />
                                </WizardTriggerMessage>
                            </WizardTrigger>
                            <WizardTrigger
                                value={WizardStep.PRICING.toString()}
                                disabled={step < WizardStep.PRICING}
                                onClick={() => goToStep(WizardStep.PRICING)}
                                className="px-4"
                            >
                                <WizardTriggerNode aria-invalid={isAriaInvalid(WizardStep.PRICING)}>
                                    {WizardStep.PRICING}
                                </WizardTriggerNode>
                                <WizardTriggerMessage>
                                    <FormattedMessage {...membershipTypeMessages.price} />
                                </WizardTriggerMessage>
                            </WizardTrigger>
                            <WizardTrigger
                                value={WizardStep.VALIDITY.toString()}
                                disabled={step < WizardStep.VALIDITY}
                                onClick={() => goToStep(WizardStep.VALIDITY)}
                                className="px-4"
                            >
                                <WizardTriggerNode aria-invalid={isAriaInvalid(WizardStep.VALIDITY)}>
                                    {WizardStep.VALIDITY}
                                </WizardTriggerNode>
                                <WizardTriggerMessage>
                                    <FormattedMessage {...membershipTypeMessages.validity} />
                                </WizardTriggerMessage>
                            </WizardTrigger>
                            <WizardTrigger
                                value={WizardStep.OVERVIEW.toString()}
                                disabled={step < WizardStep.OVERVIEW}
                                className="pl-4"
                            >
                                <WizardTriggerNode>{WizardStep.OVERVIEW}</WizardTriggerNode>
                                <WizardTriggerMessage>
                                    <FormattedMessage {...membershipTypeMessages.overview} />
                                </WizardTriggerMessage>
                            </WizardTrigger>
                        </WizardList>
                    </DialogHeader>
                    <DialogSeparator />
                    <DialogBody>
                        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
                            <WizardContent value={WizardStep.BASICS.toString()}>
                                <BasicsStep form={form} />
                            </WizardContent>
                            <WizardContent value={WizardStep.ACCESS.toString()}>
                                <AccessStep form={form} />
                            </WizardContent>
                            <WizardContent value={WizardStep.PRICING.toString()}>
                                <PricingStep form={form} />
                            </WizardContent>
                            <WizardContent value={WizardStep.VALIDITY.toString()}>
                                <ValidityStep form={form} />
                            </WizardContent>
                            <WizardContent value={WizardStep.OVERVIEW.toString()}>
                                <OverviewStep form={form} />
                            </WizardContent>
                        </Form>
                    </DialogBody>
                    <DialogSeparator />
                    <DialogFooter className="sm:justify-between">
                        <Button variant="outline" type="button" onClick={goToPreviousStep}>
                            {step === WizardStep.BASICS ? (
                                <FormattedMessage {...generalMessages.cancel} />
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={ICON_MAP.CHEVRON_LEFT} />
                                    <FormattedMessage {...commonMessages.back} />
                                </>
                            )}
                        </Button>
                        <Button
                            variant="default"
                            disabled={
                                isPending ||
                                (selectedMembershipTemplate && !form.formState.isDirty && step === WizardStep.OVERVIEW)
                            }
                            type="submit"
                            onClick={step === WizardStep.OVERVIEW ? form.handleSubmit(onSubmit) : goToNextStep}
                        >
                            {step === WizardStep.OVERVIEW ? (
                                <>
                                    {isPending ? (
                                        <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="animate-spin" />
                                    ) : (
                                        <FontAwesomeIcon icon={ICON_MAP.CHECK} />
                                    )}
                                    {title}
                                </>
                            ) : (
                                <>
                                    <FormattedMessage {...membershipTypeMessages.continue} />
                                    <FontAwesomeIcon icon={ICON_MAP.CHEVRON_RIGHT} />
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Wizard>
    );
}
